import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";

import { getClaimsSmartGroupsExport } from "shared/api/claims/api";
import {
  useListClaimsCount,
  useListClaimsSmartGroups,
} from "shared/api/claims/hooks";
import { UseLoadMoreState } from "shared/api/hooks";
import { getSignalEventsSmartGroupsExport } from "shared/api/signalEvents/api";
import {
  useListSignalEventsOccurrencesCount,
  useListSignalEventsSmartGroups,
} from "shared/api/signalEvents/hooks";
import { useCustomLocalStorageState, useTenantMileageUnit } from "shared/hooks";
import { EventTypeEnum } from "shared/types";

import { CLAIMS_TABLE_PAGE_KEY } from "pages/ClaimAnalytics/constants";
import { MainTableComponentProps } from "pages/Dashboard/types";
import { getDashboardInvestigatorSearch } from "pages/Dashboard/utils";
import { UNGROUPED_SMART_GROUPS_FILTER } from "pages/shared/Dashboard/smartGroups";
import { SIGNAL_EVENTS_TABLE_PAGE_KEY } from "pages/SignalEventsAnalytics/constants";

import Button from "features/components/Button";
import APIError from "features/ui/APIError";
import DownloadAction from "features/ui/DownloadAction";
import {
  getFiltersQuery,
  mergeFilterGroupStates,
} from "features/ui/Filters/FilterBuilder/utils";

import { routes } from "services/routes";

import {
  ADDITIONAL_LOAD_COUNT,
  CountTypeEnum,
  getCountTypeOptions,
  INITIAL_SMART_GROUP_COUNT,
  MAX_LIMIT,
} from "./constants";
import EventsSmartGroupsTableSettings from "./EventsSmartGroupsTableSettings";
import ClaimsTable from "./EventTables/ClaimsTable";
import SignalEventsTable from "./EventTables/SignalEventsTable";
import SmartGroupsTable from "./SmartGroupsTable";
import { ChartTimeGrouping, SmartGroupData } from "./SmartGroupsTable/types";
import { getChartDateFieldValue, getEventLabels } from "./utils";

interface EventsSmartGroupsProps extends MainTableComponentProps {
  eventType: EventTypeEnum;
}

// Maps the table's count-type toggle to the server-side ranking sort (`<metric>:<direction>`), so
// pagination orders groups by the same metric the user is viewing. FRC impact is negative, so its
// most-impactful-first order is ascending; the counts rank descending.
const countTypeToSort = (countType: CountTypeEnum): string => {
  switch (countType) {
    case CountTypeEnum.VEHICLE_COUNT:
      return "vehicleCount:desc";
    case CountTypeEnum.FRC_IMPACT:
      return "frcImpact:asc";
    default:
      return "eventCount:desc";
  }
};

const EventsSmartGroups = ({
  eventFilters,
  vehiclesFilters,
  allTimeVehiclesFilters,
  onBadRequest,
  config,
  dashboardName,
  dateField,
  eventType,
  smartGroups,
  onSmartGroupsChange,
  sharedTableFilterState,
  dateFieldOption,
  advancedFiltersDatePeriod,
  stickyHeader,
  stickyFooter,
}: EventsSmartGroupsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showUngroupedEvents, setShowUngroupedEvents] = useState(false);
  const [countType, setCountType] = useCustomLocalStorageState<CountTypeEnum>(
    `events-smart-groups-count-type-${dashboardName}`,
    {
      defaultValue: CountTypeEnum.EVENT_COUNT,
    }
  );
  const [areSettingsOpen, setAreSettingsOpen] = useState(false);
  const [chartTimeGrouping, setChartTimeGrouping] =
    useCustomLocalStorageState<ChartTimeGrouping>(
      `events-smart-groups-chart-time-grouping-${dashboardName}`,
      {
        defaultValue: "week",
      }
    );

  const listSmartGroupsFilters = mergeFilterGroupStates(
    eventFilters,
    sharedTableFilterState?.filters
  );

  const baseRequestParams = {
    filter: getFiltersQuery(listSmartGroupsFilters),
    vehiclesFilter: vehiclesFilters && getFiltersQuery(vehiclesFilters),
    allVehiclesFilter:
      config.useDateFromAdvancedFilter && allTimeVehiclesFilters
        ? getFiltersQuery(allTimeVehiclesFilters)
        : undefined,
    mileageUnit: useTenantMileageUnit(),
    analytics: true,
    returnVehicles: true,
    eventType:
      config.smartGroupType && config.smartGroupType !== ""
        ? config.smartGroupType
        : undefined,
    chartDateField: getChartDateFieldValue(
      config.chartSettings.chartDateField,
      vehiclesFilters,
      config.advancedFilterDateParentAttribute,
      config.advancedFilterRelationValueAttribute,
      config.advancedFilterDateChildAttribute
    ),
    granularity: chartTimeGrouping,
    simplified: config.simplified === true,
    sort: countTypeToSort(countType),
  };

  const useListSmartGroups =
    eventType === EventTypeEnum.CLAIM
      ? useListClaimsSmartGroups
      : useListSignalEventsSmartGroups;

  const {
    data: accumulatedGroups,
    hasMore,
    loadMore,
    isLoadingInitial,
    isLoadingMore,
    error,
  } = useListSmartGroups(
    baseRequestParams,
    INITIAL_SMART_GROUP_COUNT,
    ADDITIONAL_LOAD_COUNT
  ) as UseLoadMoreState<SmartGroupData>;

  // Signal events no longer return an uncategorized row, but claims still do — keep filtering it
  // out of the grouped list (its count is fetched separately below).
  const groupedData = accumulatedGroups.filter((row) => row.groupID);

  // Total number of groups across all pages (returned by the server on every page) so the
  // "N groups" label is correct from the start instead of growing as pages load.
  const totalGroupCount =
    accumulatedGroups[0]?.totalGroupCount ?? groupedData.length;

  // Ungrouped events are no longer returned as a group; fetch their count directly.
  const useUngroupedCount =
    eventType === EventTypeEnum.CLAIM
      ? useListClaimsCount
      : useListSignalEventsOccurrencesCount;
  const { data: ungroupedCountData } = useUngroupedCount({
    filter: getFiltersQuery(
      mergeFilterGroupStates(
        UNGROUPED_SMART_GROUPS_FILTER,
        listSmartGroupsFilters
      )
    ),
    vehiclesFilter: vehiclesFilters && getFiltersQuery(vehiclesFilters),
    analytics: true,
  });
  const ungroupedCount = ungroupedCountData?.count;

  const eventLabels = getEventLabels(eventType, config.title, t);

  const ungroupedFilter = mergeFilterGroupStates(
    UNGROUPED_SMART_GROUPS_FILTER,
    listSmartGroupsFilters
  );

  // ungroupedFilter already carries the "smartGroups is null" clause.
  const onNavigateToInvestigateUngrouped = () => {
    navigate({
      pathname: generatePath(routes.investigator),
      search: getDashboardInvestigatorSearch({
        eventFilters: ungroupedFilter,
        vehiclesFilters,
        eventType,
        viewTitle: config.title,
      }),
    });
  };

  const ungroupedCustomActions = (
    <Button
      color="primary"
      size="small"
      onClick={onNavigateToInvestigateUngrouped}
      testId="dashboard-ungrouped-investigate-button"
    >
      {t("schema:dashboard.investigate")}
    </Button>
  );

  const getUngroupedTableComponent = () => {
    switch (eventType) {
      case EventTypeEnum.CLAIM:
        return (
          <ClaimsTable
            testId="smart-groups-table"
            eventFilters={ungroupedFilter}
            vehiclesFilters={vehiclesFilters}
            onBadRequest={onBadRequest}
            config={config}
            limit={MAX_LIMIT}
            hideExport
            smartGroups={smartGroups}
            onSmartGroupsChange={onSmartGroupsChange}
            dashboardName={dashboardName}
            groupedCount={totalGroupCount}
            ungroupedCount={ungroupedCount}
            showUngroupedEvents={showUngroupedEvents}
            setShowUngroupedEvents={setShowUngroupedEvents}
            pageKey={`${CLAIMS_TABLE_PAGE_KEY}_${dashboardName}_ungrouped`}
            sharedTableFilterState={sharedTableFilterState}
            customActions={ungroupedCustomActions}
            rowTestId="smart-group-action-click"
            stickyHeader={stickyHeader}
            stickyFooter={stickyFooter}
          />
        );
      case EventTypeEnum.SIGNAL_EVENT:
        return (
          <SignalEventsTable
            testId="smart-groups-table"
            eventFilters={ungroupedFilter}
            vehiclesFilters={vehiclesFilters}
            onBadRequest={onBadRequest}
            config={config}
            limit={MAX_LIMIT}
            hideExport
            smartGroups={smartGroups}
            onSmartGroupsChange={onSmartGroupsChange}
            dashboardName={dashboardName}
            groupedCount={totalGroupCount}
            ungroupedCount={ungroupedCount}
            showUngroupedEvents={showUngroupedEvents}
            setShowUngroupedEvents={setShowUngroupedEvents}
            pageKey={`${SIGNAL_EVENTS_TABLE_PAGE_KEY}_${dashboardName}_ungrouped`}
            sharedTableFilterState={sharedTableFilterState}
            customActions={ungroupedCustomActions}
            rowTestId="smart-group-action-click"
            stickyHeader={stickyHeader}
            stickyFooter={stickyFooter}
          />
        );
      default:
        return null;
    }
  };

  if (error) {
    return <APIError error={error} />;
  }

  return (
    <div className="relative">
      {showUngroupedEvents ? (
        getUngroupedTableComponent()
      ) : (
        <SmartGroupsTable
          eventFilters={eventFilters}
          vehiclesFilters={vehiclesFilters}
          allTimeVehiclesFilters={allTimeVehiclesFilters}
          onBadRequest={onBadRequest}
          config={config}
          dashboardName={dashboardName}
          dateField={dateField}
          data={groupedData}
          loading={isLoadingInitial}
          eventType={eventType}
          countType={countType}
          smartGroups={smartGroups}
          onSmartGroupsChange={onSmartGroupsChange}
          groupedCount={totalGroupCount}
          ungroupedCount={ungroupedCount}
          setShowUngroupedEvents={setShowUngroupedEvents}
          showUngroupedEvents={showUngroupedEvents}
          hasMore={hasMore}
          onShowMore={loadMore}
          isLoadingMore={isLoadingMore}
          eventLabels={eventLabels}
          sharedTableFilterState={sharedTableFilterState}
          dateFieldOption={dateFieldOption}
          chartTimeGrouping={chartTimeGrouping}
          onChartTimeGroupingChange={setChartTimeGrouping}
          advancedFiltersDatePeriod={advancedFiltersDatePeriod}
          stickyHeader={stickyHeader}
          stickyFooter={stickyFooter}
          rightContent={
            <div className="flex items-center gap-2">
              <DownloadAction
                downloadSource="smart-groups"
                disabled={!groupedData || groupedData.length === 0}
                downloadFunc={
                  eventType === EventTypeEnum.CLAIM
                    ? getClaimsSmartGroupsExport
                    : getSignalEventsSmartGroupsExport
                }
                fileName="smart-groups"
                requestParams={{
                  filter: baseRequestParams.filter,
                  vehiclesFilter: baseRequestParams.vehiclesFilter,
                }}
                entityLabel={{ key: "schema:dashboard.smart_group" }}
              />
              <EventsSmartGroupsTableSettings
                countType={countType}
                onCountTypeChange={(newCountType) => {
                  setCountType(newCountType);
                  setAreSettingsOpen(false);
                }}
                isOpen={areSettingsOpen}
                setIsOpen={setAreSettingsOpen}
                countTypeOptions={getCountTypeOptions(
                  t,
                  config.enableTableFRCImpact
                )}
              />
            </div>
          }
        />
      )}
    </div>
  );
};

export default EventsSmartGroups;
